import { supabase } from '../lib/supabase';
export const organizationService = {
    /**
     * Fetch all organizations for the current user
     */
    async fetchOrganizations() {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Fetch organizations where user is a member
            const { data, error } = await supabase
                .from('organizations')
                .select(`
          id,
          name,
          slug,
          description,
          avatar_url,
          type,
          plan,
          is_active,
          created_at
        `)
                .eq('is_active', true)
                .order('name', { ascending: true });
            if (error) {
                console.error('Error fetching organizations:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch organizations:', error);
            return [];
        }
    },
    /**
     * Fetch organizations where user is a member
     */
    async fetchUserOrganizations() {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Fetch organizations where user is a member
            const { data, error } = await supabase
                .from('organization_members')
                .select(`
          organization_id,
          organizations (
            id,
            name,
            slug,
            description,
            avatar_url,
            type,
            plan,
            is_active,
            created_at
          )
        `)
                .eq('user_id', user.id)
                .eq('organizations.is_active', true);
            if (error) {
                console.error('Error fetching user organizations:', error);
                throw error;
            }
            // Extract organizations from the join result
            const organizations = data
                ?.map((item) => item.organizations)
                .filter(Boolean) || [];
            return organizations;
        }
        catch (error) {
            console.error('Failed to fetch user organizations:', error);
            return [];
        }
    },
    /**
     * Get organization by ID
     */
    async getOrganization(id) {
        try {
            const { data, error } = await supabase
                .from('organizations')
                .select('*')
                .eq('id', id)
                .eq('is_active', true)
                .single();
            if (error) {
                console.error('Error fetching organization:', error);
                throw error;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to fetch organization:', error);
            return null;
        }
    },
    /**
     * Update organization
     */
    async updateOrganization(id, updates) {
        try {
            const { data, error } = await supabase
                .from('organizations')
                .update(updates)
                .eq('id', id)
                .select()
                .single();
            if (error) {
                console.error('Error updating organization:', error);
                return null;
            }
            return data;
        }
        catch (error) {
            console.error('Error updating organization:', error);
            return null;
        }
    },
    /**
     * Create a new organization
     */
    async createOrganization(data) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Create organization
            const { data: organization, error: orgError } = await supabase
                .from('organizations')
                .insert({
                name: data.name,
                slug: data.slug,
                description: data.description || null,
                type: data.type,
                plan: data.plan,
                is_active: true,
                owner_id: user.id
            })
                .select()
                .single();
            if (orgError) {
                throw orgError;
            }
            // Add creator as owner to organization_members
            const { error: memberError } = await supabase
                .from('organization_members')
                .insert({
                organization_id: organization.id,
                user_id: user.id,
                role: 'owner'
            });
            if (memberError) {
                console.warn('Failed to add user as organization member:', memberError);
            }
            return organization;
        }
        catch (error) {
            console.error('Failed to create organization:', error);
            return null;
        }
    },
    /**
     * Delete organization (soft delete by setting is_active to false)
     */
    async deleteOrganization(id) {
        try {
            const { error } = await supabase
                .from('organizations')
                .update({ is_active: false })
                .eq('id', id);
            if (error) {
                console.error('Error deleting organization:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error deleting organization:', error);
            return false;
        }
    }
};
